import { ApiRequestError, apiFetch } from './client';
import type { FieldDefinition, FormLayout, FormLogo, FormRequiredStyle, FormRule, FormUploadSettings, FormWidget, GradientDir, LogoAlign } from '../shared/types';

/** Why a form is not collecting. Mirrors the server's ClosedCode. */
export type ClosedCode = 'volume' | 'disabled' | 'not_open_yet' | 'closed_schedule' | 'closed_quota';

export interface PublicFormSettings {
  theme?: {
    accent?: string; accent2?: string; accentGradient?: GradientDir;
    bg?: string; headerTitle?: string; headerSubtitle?: string; maxWidth?: number;
    logos?: FormLogo[];
    logo?: { url: string; height?: number; align?: LogoAlign };
    font?: { body?: string; heading?: string };
    mode?: 'light' | 'dark' | 'auto';
    presetId?: string;
  };
  /** The owner's open window / registration cap — drives the closed screens. */
  schedule?: { opensAt?: string; closesAt?: string; maxSubmissions?: number; closedMessage?: string };
  successMessage?: string;
  submitLabel?: string;
  completion?: {
    mode?: 'message' | 'redirect';
    title?: string;
    body?: string;
    redirectUrl?: string;
    redirectDelay?: number;
    includeSubmissionId?: boolean;
    allowAnother?: boolean;
    allowEdit?: { ttlHours: number };
    /**
     * Only ever on a submit's `completion`, from a workflow ending that
     * continues into another form: the button's words, and "skip this page".
     */
    continueLabel?: string;
    continueStraight?: boolean;
  };
  uploads?: FormUploadSettings;
  requiredStyle?: FormRequiredStyle;
  /** D15: the opt-in summary of what to fix at the top of the page */
  errorSummary?: boolean;
}

export interface PublicFormResponse {
  success: boolean;
  form: { name: string; slug: string; status: string; settings: PublicFormSettings };
  /**
   * Set when the form is not collecting right now. The billing-driven values
   * ('volume' / 'disabled') render the same cause-neutral screen and carry no
   * reason a visitor could bill by. The schedule values are the owner's OWN
   * closes, so those screens may speak: opens-at date, or the owner's
   * closedMessage from settings.schedule.
   */
  closed?: ClosedCode;
  /** A way to reach whoever runs the form, for the closed screen. */
  contact?: string | null;
  /**
   * Show the "Powered by Form-Magic.com" badge under the form?
   *
   * A bare boolean on purpose: the server has already weighed the owner's
   * setting against their plan, and a respondent is never told which of the
   * two decided it. Absent (a server from before the badge) reads as `true`.
   */
  advertisement?: boolean;
  /**
   * Test mode (R9): present only when the page was opened with a `?test=`
   * token. Good → the form it is for and when it stops working; otherwise
   * the link has expired and the submit route will refuse it.
   */
  test?: { formId: number; expiresAt: string } | { expired: true };
  passwordRequired: boolean;
  locked: boolean;
  /** Bot protection (U6): present only when this form asks for a captcha and the platform can check one. */
  captcha?: { provider: 'turnstile'; siteKey: string };
  /** Once per browser (U6): present when on, with the message a returning browser sees. */
  oncePerBrowser?: { message: string };
  /** Present when the form carries a payment field: can it charge right now? */
  payments?: { ready: boolean };
  /** Which gate stands in front of this form, when one does. */
  gate?: 'password' | 'sso' | 'magic_link' | 'invite';
  /** magic_link: how long an emailed link stays good for. */
  ttlMinutes?: number;
  provider?: { name: string; loginUrl: string };
  buttonLabel?: string;
  /** Set when the form is misconfigured — shown instead of a sign-in button. */
  gateError?: string;
  /** Present once a sign-in has been completed for this form. */
  identity?: { email?: string; name?: string; providerName?: string };
  /**
   * Values already filled in — from the signed-in account, the order this
   * form continues, or the link itself (P1) — keyed by field key or
   * composite part (`addr.city`). `lockedFields` are locked on screen and
   * re-applied server-side; `softPrefill` names the keys an UNSIGNED link
   * filled, which a restored draft keeps over; `linkLocked` the keys a
   * signed link locked.
   */
  prefill?: Record<string, unknown>;
  lockedFields?: string[];
  softPrefill?: string[];
  linkLocked?: string[];
  /**
   * Answers carried over from the submission this form continues (the order handoff), with
   * how each must be treated. `prefill` and `lockedFields` above already carry
   * the values and the locks; this says where they came from and which of them
   * the person has to give a verdict on.
   */
  carried?: Record<string, { from: unknown; mode: 'locked' | 'confirm' | 'editable' }>;
  confirmFields?: string[];
  confirmPrompt?: string;
  /**
   * The order's own answers, for the rules that name them: the `parent` scope
   * a continuing form's Logic weighs (`parent.orderType`, and one dot deeper
   * into a composite, `parent.shipAddress.state`).
   *
   * Narrowed server-side to the paths the published rules actually read — the
   * rest of the order never leaves the server. Unlike `prefill`, nothing here
   * is an answer: it is never written into an input, never editable, and never
   * comes back on the submission.
   */
  parent?: Record<string, unknown>;
  /**
   * A draft saved against this form's invite, from any device (038). Offered
   * behind the same "Resume?" prompt as the browser's own — never applied
   * silently, since the person may be back to start again on purpose.
   */
  partial?: { data: unknown; savedAt: string };
  /** "Save and continue later" (P6) is on offer: this server can email a resume link. */
  resume?: { available: true };
  // Only sent once the gate has been passed.
  fields?: FieldDefinition[];
  layout?: FormLayout;
  rules?: FormRule[];
  widgets?: FormWidget[];
}

/**
 * A public form path is one or two segments — "slug" or "account/slug".
 * encodeURIComponent on the whole thing turns the separator into %2F and the
 * request 404s, so each segment is encoded on its own.
 */
const formPath = (p: string) => p.split('/').map(encodeURIComponent).join('/');

/**
 * `invite` is forwarded as a query parameter because the server redeems it into
 * the session on this very request. The page's address bar is cleaned once the
 * response lands, so the token has to travel here or it never travels at all.
 *
 * `search` is the rest of the address bar's query (P1): the prefill params,
 * and a signed link's `t` and `sig`, which the server resolves on this GET.
 */
export const getPublicForm = (slug: string, invite?: string, search?: string) => {
  const q = new URLSearchParams(search || '');
  if (invite) q.set('invite', invite);
  const qs = q.toString();
  return apiFetch<PublicFormResponse>(`/public/forms/${formPath(slug)}${qs ? `?${qs}` : ''}`);
};

export const enterFormPassword = (slug: string, password: string) =>
  apiFetch<{ success: boolean }>(`/public/forms/${formPath(slug)}/access`, {
    method: 'POST', body: JSON.stringify({ password }),
  });

/**
 * Ask for a sign-in link. Always resolves the same way whether or not anything
 * was sent — the server will not confirm which addresses exist.
 */
export const requestMagicLink = (slug: string, email: string) =>
  apiFetch<{ success: boolean; sent: boolean }>(`/public/forms/${formPath(slug)}/magic-link`, {
    method: 'POST', body: JSON.stringify({ email }),
  });

export const signOutOfForm = (slug: string) =>
  apiFetch<{ success: boolean }>(`/public/forms/${formPath(slug)}/signout`, { method: 'POST' });

/**
 * A submit can come back `200` with `closed` instead of a submission id — the
 * form stopped collecting while the person was typing. Never a 402: the
 * customer's billing state is not their visitor's business.
 */
export const submitPublicForm = (
  slug: string,
  data: Record<string, unknown>,
  honeypot = '',
  /** Verdicts on carried-over answers (the order handoff) — a sibling of `data`, not an answer. */
  confirmations?: Record<string, boolean>,
  /**
   * How each signature was made, by field key — a sibling of `data` for the
   * same reason. The server narrows it and records it; it never becomes an
   * answer, and nothing here decides whether a signature is valid.
   */
  signatureModes?: Record<string, { mode: 'draw' | 'type'; typedName?: string }>,
  /** The Turnstile token, when the form asked for a captcha (U6). */
  captcha?: string,
  /** Tracking params (`utm_*`, `ref`, …) read off the address bar when the form opened (P1 / A5). */
  tracking?: Record<string, string>,
  /** The `?test=` token the builder's Test mode opened the page with (R9): the row is saved as a test. */
  test?: string,
  /**
   * The answers that were not on screen when this was sent, by path
   * (`hiddenAtSubmit`) — a sibling of `data`: a fact about answers, not one.
   * The PDF's list of answers leaves them off.
   */
  hidden?: string[],
) =>
  apiFetch<{
    success: boolean;
    submissionId?: string;
    deliveries?: { type: string; status: string }[];
    /**
     * The invite link, handed back rather than emailed — the sales rep chose to
     * enter the location details themselves. Absent on the customer path.
     */
    continueUrl?: string;
    /**
     * A private, short-lived link to a PDF of this submission — sent only when
     * the ending it reached offers the download. Absent otherwise.
     */
    documentUrl?: string;
    /**
     * A signed link to edit this submission (P6), relative like `documentUrl`,
     * and when it stops working. Sent only when the ending reached offers it.
     */
    editUrl?: string;
    editUntil?: string;
    /**
     * The ending this submission's path reached (043).
     *
     * Only sent when a workflow decided it. Absent means the form's own
     * After-submitting setting stands, which is what every form without one
     * gets — so the response a list-runner form gets is unchanged.
     */
    completion?: PublicFormSettings['completion'];
    /**
     * Delivery results the thank-you page may interpolate. Narrow by design:
     * only what a connection's spec declares, and only what this form's
     * completion settings actually name.
     */
    tokens?: Record<string, unknown>;
    /** present when the submission owes money — send the person here to pay */
    payment?: { checkoutUrl: string; amountCents: number; currency: string };
    closed?: ClosedCode;
    contact?: string | null;
    /** the row was saved as a test (R9): where to see it */
    test?: { formId: number; submissionId: number };
  }>(
    `/public/forms/${formPath(slug)}/submissions`,
    {
      method: 'POST',
      body: JSON.stringify({
        data, _hp: honeypot,
        ...(confirmations && Object.keys(confirmations).length ? { confirmations } : {}),
        ...(signatureModes && Object.keys(signatureModes).length ? { signatureModes } : {}),
        ...(captcha ? { _captcha: captcha } : {}),
        ...(tracking && Object.keys(tracking).length ? { tracking } : {}),
        ...(test ? { _test: test } : {}),
        ...(hidden && hidden.length ? { hidden } : {}),
      }),
    });

/** Confirm a Stripe Checkout return and mark the submission paid. */
export const verifyPublicPayment = (slug: string, sessionId: string) =>
  apiFetch<{ success: boolean; paid: boolean; submissionId: string }>(
    `/public/forms/${formPath(slug)}/payments/verify`,
    { method: 'POST', body: JSON.stringify({ sessionId }) });

/**
 * Autosave the in-progress answers server-side. Only invite forms have one;
 * for everything else the server answers 204 and the browser's localStorage
 * draft is the whole story. Fire-and-forget: a failed autosave must never
 * interrupt somebody typing.
 */
export const savePublicPartial = (slug: string, payload: unknown) =>
  apiFetch<void>(`/public/forms/${formPath(slug)}/partial`, {
    method: 'PUT', body: JSON.stringify(payload),
  }).catch(() => undefined);

/**
 * "Save and continue later" (P6): keep the draft server-side and email a
 * link that restores it anywhere. Answers the same way whether or not a mail
 * went out, like the magic-link request.
 */
export const requestResumeLink = (slug: string, email: string, draft: unknown) =>
  apiFetch<{ success: boolean; sent: boolean; expiresAt?: string }>(
    `/public/forms/${formPath(slug)}/resume`,
    { method: 'POST', body: JSON.stringify({ email, draft }) });

/** What the edit page gets back for a good token (P6). */
export interface PublicEditResponse {
  success: boolean;
  form: { name: string; slug: string; status: string; settings: PublicFormSettings };
  fields: FieldDefinition[];
  layout: FormLayout;
  rules: FormRule[];
  widgets: FormWidget[];
  submissionId: string;
  /** the stored answers, minus order-scope files (described in `provided`) */
  data: Record<string, unknown>;
  /** file fields already answered: key -> "a.pdf, b.png" */
  provided: Record<string, string>;
  /** answers the respondent may not change (claims, carried locks, a signed link's locks) */
  lockedFields: string[];
  expiresAt: string;
  /**
   * The order this submission continues, for the rules that name it — the
   * same read-only `parent` scope the form was filled under, so re-opening it
   * shows the same fields. Read off the submission's own parent link rather
   * than a session invite; see the edit route.
   */
  parent?: Record<string, unknown>;
}

/** The definition plus the stored answers behind an edit token (P6). */
export const getPublicEdit = (slug: string, token: string) =>
  apiFetch<PublicEditResponse>(`/public/forms/${formPath(slug)}/edit/${encodeURIComponent(token)}`);

/** Save the respondent's changes as a revision (P6). */
export const putPublicEdit = (
  slug: string, token: string, data: Record<string, unknown>,
  signatureModes?: Record<string, { mode: 'draw' | 'type'; typedName?: string }>,
  /** What was hidden on this edit's screen — always sent, an empty list included, since it replaces the last one. */
  hidden: string[] = [],
) =>
  apiFetch<{ success: boolean; submissionId: string; revision?: number; changed: string[] }>(
    `/public/forms/${formPath(slug)}/edit/${encodeURIComponent(token)}`,
    {
      method: 'PUT',
      body: JSON.stringify({
        data,
        ...(signatureModes && Object.keys(signatureModes).length ? { signatureModes } : {}),
        hidden,
      }),
    });

/** Throw the server-side draft away — the "start over" half of the prompt. */
export const clearPublicPartial = (slug: string) =>
  apiFetch<void>(`/public/forms/${formPath(slug)}/partial`, { method: 'DELETE' })
    .catch(() => undefined);

/**
 * Upload one field's files. XMLHttpRequest rather than fetch for the one
 * thing fetch cannot do: report progress on the way up. A refusal throws an
 * ApiRequestError whose body carries the server's `errors[]` (field, kind,
 * message), so the page lands it under the control like any other refusal.
 */
export function uploadPublicFiles(
  slug: string, fieldKey: string, files: File[], onProgress?: (loaded: number, total: number) => void
): Promise<{ name: string; url: string }[]> {
  if (!files.length) return Promise.resolve([]);
  const fd = new FormData();
  files.forEach((f) => fd.append('files', f));
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `/api/public/forms/${formPath(slug)}/uploads?field=${encodeURIComponent(fieldKey)}`);
    xhr.withCredentials = true;
    xhr.responseType = 'text';
    if (onProgress) xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded, e.total); };
    xhr.onerror = () => reject(new ApiRequestError('Upload failed', 0));
    xhr.onload = () => {
      let json: any = null;
      try { json = xhr.responseText ? JSON.parse(xhr.responseText) : null; } catch { json = null; }
      if (xhr.status >= 200 && xhr.status < 300 && json?.success) { resolve(json.files || []); return; }
      const message = (json && (json.error || json.message)) || `Upload failed (${xhr.status})`;
      reject(new ApiRequestError(message, xhr.status, json));
    };
    xhr.send(fd);
  });
}
